import * as z from "zod";
import { pagination } from "../index";
import { type RetryWaitingInput, retryWaitingDto, retryWaitingInput, waitingItemCodes, waitingSources } from "../til";
import { movementExecutionWaitingReasons, movementSteps } from "./enums";

// What the warehouse did of a movement of stock (I-11 `kind: movement`, ADR-142; 50-api/logistics.md): each execution
// adds its units to the lines of the movement, on the step it names, and the movement is executed once the warehouse
// closed both steps. An execution Nexxt cannot apply waits whole until the Logistics sets right what is missing.

/** An execution of the warehouse, as the IMS reconciled it or GXO returned it (ADR-144): the units of one step, in one message. */
export const movementExecutionDto = z.object({
  id: z.uuid(),
  /** Its number in the system of the warehouse. */
  movementRef: z.string(),
  step: z.enum(movementSteps),
  executedAt: z.iso.datetime(),
  /** The warehouse expects nothing more of the step. */
  stepClosed: z.boolean(),
  /** The units of all its lines. */
  units: z.number().int(),
});

// --- Executions of the warehouse Nexxt could not apply (I-11 §5) ---

const waitingFilters = { reason: z.enum(movementExecutionWaitingReasons).optional() };

/** The executions of the warehouse that wait, the latest first: each lists at most 50 of its items (`itemCount`). */
export const listWaitingMovementExecutionsInput = z.object({
  ...waitingFilters,
  ...pagination({ defaultSize: 50, maxSize: 100 }),
});
export type ListWaitingMovementExecutionsInput = z.input<typeof listWaitingMovementExecutionsInput>;

/** How many executions wait with the same filters, and since when the oldest (ADR-086): a queue of the home. */
export const countWaitingMovementExecutionsInput = z.object(waitingFilters);
export type CountWaitingMovementExecutionsInput = z.input<typeof countWaitingMovementExecutionsInput>;

/**
 * Applies again the executions that wait, once the mapping, the SKU or the movement is set right: each one applied
 * goes, the others wait with the reason of now.
 */
export const retryWaitingMovementExecutionsInput = retryWaitingInput;
export type RetryWaitingMovementExecutionsInput = RetryWaitingInput;
export const retryWaitingMovementExecutionsDto = retryWaitingDto("WaitingMovementExecutionsRetried");

/** An item the reason of an execution that waits is about, with its codes as the warehouse sent them. */
export const waitingMovementExecutionItem = z.object(waitingItemCodes);
export type WaitingMovementExecutionItem = z.output<typeof waitingMovementExecutionItem>;

/** An execution of the warehouse that waits, whole: nothing of it is applied until all of it can be. */
export const waitingMovementExecutionDto = z
  .object({
    id: z.uuid(),
    movementRef: z.string(),
    /** The number of the movement, as the warehouse sent it. */
    movementNumber: z.string(),
    step: z.enum(movementSteps),
    /** The system the fact came from (ADR-144): the warehouse and the items are in its codes. */
    source: z.enum(waitingSources),
    /** The warehouse in the codes of `source`. */
    locationCode: z.string(),
    executedAt: z.iso.datetime(),
    stepClosed: z.boolean(),
    reason: z.enum(movementExecutionWaitingReasons),
    /** The first of the items the reason is about: none for the execution as a whole. */
    items: z.array(waitingMovementExecutionItem),
    /** How many items the reason is about. */
    itemCount: z.number().int(),
    /** Since when it waits. */
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: "WaitingMovementExecution" });
